import { useState } from "react";
import { Flex } from "@radix-ui/themes";
import { InlineTextField } from "@/components/shared/inputs/inilie-text-field/InlineTextField";
import Select, { SelectOption } from "@/components/shared/select";
import {
  PreChatAvatarRadius,
  preChatAvatarRadiuses,
} from "@/components/quack-chat/QuackChatConfig.model";
import { UploadQuackchatAssestResponse } from "@/api/quackchat/configuration/useUploadQuackchatAssest";
import { CollapsibleFieldList } from "../../../parts/CollapsibleFieldList";
import { FieldGroup, OptionalFieldGroup } from "../../../parts/FieldGroup";
import { FileUploadComponent } from "../../../parts/FileUploadComponent";
import styles from "../../../AutomationConfiguration.module.scss";

const avatarRadiusOptions: SelectOption[] = [
  { label: "Regular", value: "regular" },
  { label: "Rounded", value: "rounded" },
];

function isValidAvatarRadius(value: string): value is PreChatAvatarRadius {
  return (preChatAvatarRadiuses as readonly string[]).includes(value);
}

interface PreChatFlowSettingsProps {
  enabled: boolean;
  welcomeMessage: string;
  welcomeSubtitle: string;
  caseTitle: string;
  avatarSVG: string;
  avatarRadius: PreChatAvatarRadius;
  onUploadAvatar: (file: File) => Promise<UploadQuackchatAssestResponse>;
  onEnabledChange: (nextValue: boolean) => void;
  onWelcomeMessageChange: (value: string) => void;
  onWelcomeSubtitleChange: (value: string) => void;
  onCaseTitleChange: (value: string) => void;
  onAvatarChange: (value: string) => void;
  onAvatarRadiusChange: (value: PreChatAvatarRadius) => void;
}

export function PreChatFlowSettings({
  enabled,
  welcomeMessage,
  welcomeSubtitle,
  caseTitle,
  avatarSVG,
  avatarRadius,
  onUploadAvatar,
  onEnabledChange,
  onWelcomeMessageChange,
  onWelcomeSubtitleChange,
  onCaseTitleChange,
  onAvatarChange,
  onAvatarRadiusChange,
}: PreChatFlowSettingsProps) {
  const [avatarError, setAvatarError] = useState<string>();

  return (
    <CollapsibleFieldList title={"Pre-Chat Flow"} defaultOpen>
      <OptionalFieldGroup
        label={"Show Pre-Chat Flow"}
        description={"Show a pre-chat form before entering the chat experience."}
        enabled={enabled}
        toggleEnabled={onEnabledChange}
      />

      {enabled && (
        <Flex width="100%" direction="column">
          <FieldGroup horizontal description={"Welcome title"}>
            <InlineTextField
              value={welcomeMessage}
              onChange={onWelcomeMessageChange}
              placeholder={"Welcome 👋"}
            />
          </FieldGroup>

          <FieldGroup horizontal description={"Welcome subtitle"}>
            <InlineTextField
              value={welcomeSubtitle}
              onChange={onWelcomeSubtitleChange}
              placeholder={"Let's get started! Please fill in your details below."}
            />
          </FieldGroup>

          <FieldGroup horizontal description={"Case form title"}>
            <InlineTextField
              value={caseTitle}
              onChange={onCaseTitleChange}
              placeholder={"How can we help you today?"}
            />
          </FieldGroup>

          <FieldGroup horizontal description={"Avatar"} error={avatarError}>
            <FileUploadComponent
              key={avatarSVG}
              defaultFileName={avatarSVG.split("/").pop() || undefined}
              onFileUpload={onUploadAvatar}
              onSuccess={(filePath) => {
                setAvatarError(undefined);
                onAvatarChange(filePath);
              }}
              onError={setAvatarError}
            />
          </FieldGroup>

          <FieldGroup horizontal description={"Avatar radius"}>
            <Select
              value={avatarRadius}
              onChange={(newValue) => {
                onAvatarRadiusChange(
                  isValidAvatarRadius(newValue) ? newValue : "regular"
                );
              }}
              options={avatarRadiusOptions}
              classes={{
                trigger: styles.Select,
                value: styles.SelectValue,
              }}
            />
          </FieldGroup>
        </Flex>
      )}
    </CollapsibleFieldList>
  );
}
